import type React from "react";

import type {
  PersonNameChildAttribute,
  PersonNameValue,
} from "@/components/Input/PersonName/types";
import { PhoneValue } from "@/components/Input/Phone/state.machine";
import { VStack } from "@/components/Layout";
import { Text } from "@/components/Typography/Text";
import { SupportedCountry } from "@/constants/country";
import type {
  ApiResponseBody,
  ApiSearchQuery,
} from "@/lib/api/api-type-helper";
import { defaultNS, useTranslation } from "@/lib/i18n";

import { CFormCountryItem } from "../CFormItems/impl/CFormCountryItem";
import { CFormDateItem } from "../CFormItems/impl/CFormDateItem";
import { CFormEmailItem } from "../CFormItems/impl/CFormEmailItem";
import { CFormNumberItem } from "../CFormItems/impl/CFormNumberItem";
import { CFormPersonNameItem } from "../CFormItems/impl/CFormPersonNameItem";
import { CFormPersonSelectItem } from "../CFormItems/impl/CFormPersonSelectItem";
import {
  CFormPhoneItem,
  JAPAN_DIAL_CODE,
} from "../CFormItems/impl/CFormPhoneItem";
import { CFormPlaceMultiSelectItem } from "../CFormItems/impl/CFormPlaceMultiSelectItem";
import { CFormPlaceSelectItem } from "../CFormItems/impl/CFormPlaceSelectItem";
import { CFormPostalCodeItem } from "../CFormItems/impl/CFormPostalCodeItem";
import { CFormSelectItem } from "../CFormItems/impl/CFormSelectItem";
import { CFormSelectItems } from "../CFormItems/impl/CFormSelectItems";
import { CFormStateProvinceItem } from "../CFormItems/impl/CFormStateProvinceItem";
import { CFormSwitchItem } from "../CFormItems/impl/CFormSwitchItem";
import { CFormTextAreaItem } from "../CFormItems/impl/CFormTextAreaItem";
import { CFormTextItem } from "../CFormItems/impl/CFormTextItem";
/**
 * SectionSchema API のレスポンス型
 */
type SectionSchemaResponse = ApiResponseBody<"/schema/section", "get">;

/**
 * セクションの型定義（バックエンドスキーマから取得）
 */
export type Section = SectionSchemaResponse["sections"][number];

/**
 * 属性の型定義（バックエンドスキーマから取得）
 */
export type Attribute = Section["attributes"][number];

export type ComboboxOption = NonNullable<Attribute["options"]>[number];

/**
 * 属性の値型定義（valueType）（バックエンドスキーマから取得）
 */
export type ValueType = Attribute["valueType"];

/**
 * 属性の値型（バックエンドと整合性を持つ）
 * Note: Date型はフロントエンド固有の拡張（バックエンドはstring）
 */
// oxlint-disable-next-line typescript/no-redundant-type-constituents -- Attribute["value"] includes any
export type Value = Attribute["value"] | Date | undefined;

/**
 * ストア全体の型定義
 */
export interface StoreValues {
  sections: Section[];
}

/**
 * ストアアクションの型定義
 *
 * `value`パラメータは各エンティティの値型と互換性を持たせるため、
 * 柔軟な型定義としています。
 */
export type UpdateSectionAttribute = (
  sectionKey: string,
  attributeKey: string,
  // oxlint-disable-next-line typescript/no-redundant-type-constituents -- Value includes any
  value: Value | null,
) => void;

/**
 * ベースプロパティの型定義
 */
export interface BaseProps {
  id: string;
  label: string;
  required: boolean;
  readonly: boolean;
  colSpan: 1 | 2 | 3 | 4 | 5 | 6 | 7 | undefined;
}

/**
 * ValueType=CUSTOMを設定した項目に対して、ドメイン独自のレンダリングを実施する場合に利用する
 */
export type ProvisionalValueTypeRenderer = (
  attribute: Attribute,
  baseProps: BaseProps,
  getCurrentAttribute: (
    attributeKey: string,
    sectionKey: string,
    storeValues: StoreValues,
  ) => Attribute | undefined,
) => React.ReactElement | null;

/**
 * valueType固有のオプション
 *
 * 各valueTypeに対するカスタマイズオプションを定義します。
 * 新しいvalueTypeが追加され、オプションが必要な場合はここに追加してください。
 */
type WorkersForSelectQuery = ApiSearchQuery<"/v2/workers-for-select", "get">;
export interface ValueTypeOptions {
  /** PERSON_SELECT用: Workerカテゴリフィルター（例: "BOARD_ACTIVE" で取締役のみ） */
  personSelectCategory?: WorkersForSelectQuery["category"];
}

interface Props {
  attributes: Attribute[];
  sectionKey: string;
  updateSectionAttribute: UpdateSectionAttribute;
  storeValues: StoreValues;
  /** valueType固有のオプション */
  options?: ValueTypeOptions;
  /** ValueType=CUSTOMを設定した項目に対して、ドメイン独自のレンダリングを実施する場合に利用する */
  provisionalValueTypeRenderer?: ProvisionalValueTypeRenderer;
}

/**
 * 汎用属性レンダラー
 *
 * バックエンドのvalueTypeに基づいて動的にフォームコンポーネントを生成します。
 *
 * ## 使用例
 * ```tsx
 * <AttributeRenderer
 *   attributes={section.attributes}
 *   sectionKey={section.sectionKey}
 *   actions={store.actions}
 *   storeValues={store.values}
 * />
 * ```
 */
export function AttributeRenderer({
  attributes,
  sectionKey,
  updateSectionAttribute,
  storeValues,
  provisionalValueTypeRenderer,
  options,
}: Props) {
  const { t } = useTranslation(defaultNS, {
    keyPrefix: "features.cForm.attributeRenderer",
  });

  if (!attributes || attributes.length === 0) {
    return (
      <VStack className="items-center py-8">
        <Text variant="pSmall" className="text-neutral-muted-on-fill">
          {t("noAttributes")}
        </Text>
      </VStack>
    );
  }

  const sortedAttributes = [...attributes].sort(
    (a, b) => a.displayOrder - b.displayOrder,
  );

  const renderedComponents = sortedAttributes.map((attribute) =>
    renderAttributeComponent(
      attribute,
      sectionKey,
      updateSectionAttribute,
      storeValues,
      provisionalValueTypeRenderer,
      options,
    ),
  );

  return <>{renderedComponents}</>;
}

/**
 * 属性のvalueTypeに基づいて適切なCFormコンポーネントをレンダリング
 *
 * ## サポートされるvalueType
 * - TEXT: テキスト入力
 * - TEXT_AREA: 複数行テキスト入力
 * - NUMBER: 数値入力
 * - BOOLEAN: ブール値入力
 * - SELECT: 選択（セレクトボックス）
 * - MULTI_SELECT: 複数選択（マルチセレクト）
 * - DATE: 日付入力
 * - PERSON_SELECT: 人物選択（コンボボックス）
 * - PLACE_SELECT: 拠点選択（セレクトボックス）
 * - PLACE_MULTI_SELECT: 拠点複数選択（マルチセレクト）
 * - POSTAL_CODE: 郵便番号入力
 * - PHONE_NUMBER: 電話番号入力
 * - EMAIL_ADDRESS: メールアドレス入力
 * - PERSON_NAME: 氏名入力（姓・名・姓カナ・名カナ）
 * - COUNTRY: 国選択（セレクトボックス）
 * - STATE_PROVINCE: 都道府県選択（セレクトボックス）
 * - CUSTOM: カスタム（ドメイン独自のレンダリング）
 *
 * ## 拡張方法
 * 新しいvalueTypeを追加する場合：
 * 1. バックエンドのスキーマにvalueTypeを追加（型は自動同期）
 * 2. 対応するCFormItemコンポーネントを作成（または既存のものを使用）
 * 3. switch文に新しいcaseを追加
 */
function renderAttributeComponent(
  attribute: Attribute,
  sectionKey: string,
  updateSectionAttribute: UpdateSectionAttribute,
  storeValues: StoreValues,
  provisionalValueTypeRenderer?: ProvisionalValueTypeRenderer,
  options?: ValueTypeOptions,
): React.ReactElement | null {
  // ストアから現在の値を取得（更新された値を優先）
  const currentAttribute = getCurrentAttribute(
    attribute.attributeKey,
    sectionKey,
    storeValues,
  );
  const attr = currentAttribute || attribute;
  const attributeValue = attr.value;

  // 値変更のハンドラー
  const handleValueChange = (newValue: Value) => {
    // - undefined は null として扱う
    // - Date は ISO 文字列に変換
    // PersonNameValue型の場合はそのまま返す
    let convertedValue: string | string[] | null | PersonNameValue;
    if (newValue === undefined || newValue === null) {
      convertedValue = null;
    } else if (newValue instanceof Date) {
      convertedValue = newValue.toISOString();
    } else if (
      typeof newValue === "object" &&
      !Array.isArray(newValue) &&
      "familyName" in newValue
    ) {
      convertedValue = newValue as PersonNameValue;
    } else {
      convertedValue = newValue;
    }
    updateSectionAttribute(
      sectionKey,
      String(attribute.attributeKey),
      convertedValue,
    );
  };

  const baseProps = getBaseProps(attr);

  // valueTypeに基づいてコンポーネントを選択
  switch (attr.valueType) {
    case "DATE":
      return (
        <CFormDateItem
          key={String(attr.attributeKey)}
          {...baseProps}
          value={
            attributeValue instanceof Date
              ? attributeValue
              : typeof attributeValue === "string"
                ? attributeValue
                : undefined
          }
          onChange={(value) => handleValueChange(value)}
          allowEmpty={!attr.required}
        />
      );

    case "TEXT":
      return (
        <CFormTextItem
          key={String(attr.attributeKey)}
          {...baseProps}
          value={typeof attributeValue === "string" ? attributeValue : ""}
          onChange={(value) => handleValueChange(value ?? "")}
        />
      );

    case "TEXT_AREA":
      return (
        <CFormTextAreaItem
          key={String(attr.attributeKey)}
          {...baseProps}
          value={typeof attributeValue === "string" ? attributeValue : ""}
          onChange={(value) => handleValueChange(value ?? "")}
        />
      );

    case "NUMBER": {
      return (
        <CFormNumberItem
          key={String(attr.attributeKey)}
          {...baseProps}
          value={
            typeof attributeValue === "number" ? attributeValue : undefined
          }
          onChange={(value) => handleValueChange(value ?? null)}
        />
      );
    }

    case "BOOLEAN":
      return (
        <CFormSwitchItem
          key={String(attr.attributeKey)}
          {...baseProps}
          value={typeof attributeValue === "boolean" ? attributeValue : false}
          onChange={(value) => handleValueChange(value ?? null)}
        />
      );

    case "SELECT": {
      const displayValue =
        attr.options?.find((option) => option.value === attributeValue)
          ?.label ?? "";
      return (
        <CFormSelectItem
          key={String(attr.attributeKey)}
          {...baseProps}
          displayValue={displayValue}
          value={typeof attributeValue === "string" ? attributeValue : ""}
          onChange={(value) => handleValueChange(value ?? "")}
          fetchOptions={async () => attr.options ?? []}
        />
      );
    }

    case "MULTI_SELECT":
      return (
        <CFormSelectItems<ComboboxOption>
          key={String(attr.attributeKey)}
          {...baseProps}
          value={Array.isArray(attributeValue) ? attributeValue : []}
          onChange={(value) => handleValueChange(value ?? [])}
          fetchOptions={async () => attr.options ?? []}
          renderOption={({ option }: { option: ComboboxOption }) => (
            <div>
              {option.label}
              {option.descriptionLabel && (
                <Text variant="pSmall" className="text-neutral-muted-on-fill">
                  {option.descriptionLabel}
                </Text>
              )}
            </div>
          )}
        />
      );

    case "PERSON_SELECT": {
      const personId =
        typeof attributeValue === "string" ? attributeValue : undefined;
      return (
        <CFormPersonSelectItem
          key={String(attr.attributeKey)}
          {...baseProps}
          value={personId}
          onChange={(value) => handleValueChange(value ?? null)}
          showProfileLink={true}
          category={options?.personSelectCategory}
          allowEmpty={!attr.required}
        />
      );
    }

    case "PLACE_SELECT": {
      const placeId =
        typeof attributeValue === "string" ? attributeValue : undefined;
      return (
        <CFormPlaceSelectItem
          key={String(attr.attributeKey)}
          {...baseProps}
          value={placeId}
          onChange={(value) => handleValueChange(value ?? null)}
          showPlaceLink={true}
          allowEmpty={!attr.required}
        />
      );
    }

    case "PLACE_MULTI_SELECT": {
      const placeIds = Array.isArray(attributeValue)
        ? attributeValue
        : undefined;
      return (
        <CFormPlaceMultiSelectItem
          key={String(attr.attributeKey)}
          {...baseProps}
          value={placeIds}
          onChange={(value) => handleValueChange(value ?? null)}
          showPlaceLink={true}
          allowEmpty={!attr.required}
        />
      );
    }

    case "POSTAL_CODE":
      return (
        <CFormPostalCodeItem
          key={String(attr.attributeKey)}
          {...baseProps}
          value={typeof attributeValue === "string" ? attributeValue : ""}
          onChange={(value) => handleValueChange(value ?? "")}
        />
      );

    case "PHONE_NUMBER": {
      // 電話番号は { dialCode: string, phoneNumber: string } 形式で管理
      const phoneNumberValue: PhoneValue =
        attributeValue &&
        typeof attributeValue === "object" &&
        !Array.isArray(attributeValue)
          ? (attributeValue as PhoneValue)
          : {
              dialCode: JAPAN_DIAL_CODE,
              phoneNumber: "",
            };
      return (
        <CFormPhoneItem
          key={String(attr.attributeKey)}
          {...baseProps}
          value={phoneNumberValue}
          onChange={(value) => handleValueChange(value)}
        />
      );
    }

    case "EMAIL_ADDRESS":
      return (
        <CFormEmailItem
          key={String(attr.attributeKey)}
          {...baseProps}
          value={typeof attributeValue === "string" ? attributeValue : ""}
          onChange={(value) => handleValueChange(value ?? "")}
        />
      );

    case "PERSON_NAME": {
      const personNameValue: PersonNameValue =
        attributeValue &&
        typeof attributeValue === "object" &&
        !Array.isArray(attributeValue)
          ? (attributeValue as PersonNameValue)
          : {
              familyName: "",
              givenName: "",
              familyNameKana: "",
              givenNameKana: "",
            };
      // バックエンドのchildrenをPersonNameChildAttribute型にキャスト
      // fieldKeyはバックエンドが正しい値（familyName等）を返す前提
      const childAttributes = attr.children as
        | PersonNameChildAttribute[]
        | undefined;
      return (
        <CFormPersonNameItem
          key={String(attr.attributeKey)}
          {...baseProps}
          value={personNameValue}
          onChange={(value) => handleValueChange(value)}
          childAttributes={childAttributes}
        />
      );
    }

    case "COUNTRY": {
      return (
        <CFormCountryItem
          key={String(attr.attributeKey)}
          {...baseProps}
          value={
            typeof attributeValue === "string"
              ? { code: attributeValue }
              : undefined
          }
          onChange={(value) => handleValueChange(value?.code ?? "")}
        />
      );
    }

    case "STATE_PROVINCE": {
      // 同じSection内からCountryを見つける
      const countryCode = findAttributeByValueType(
        "COUNTRY",
        sectionKey,
        storeValues,
      )?.value as SupportedCountry | undefined;

      return (
        <CFormStateProvinceItem
          key={String(attr.attributeKey)}
          countryCode={countryCode}
          {...baseProps}
          value={typeof attributeValue === "string" ? attributeValue : ""}
          onChange={(value) => handleValueChange(value ?? "")}
        />
      );
    }

    case "PROVISIONAL": {
      return (
        provisionalValueTypeRenderer?.(attr, baseProps, getCurrentAttribute) ??
        null
      );
    }

    // 将来的な拡張: FILE など
    default:
      // 未対応のvalueTypeはテキストとして表示
      console.warn(
        `Unsupported valueType: ${attr.valueType} for attribute: ${String(attr.attributeKey)}`,
      );
      return (
        <CFormTextItem
          key={String(attr.attributeKey)}
          {...baseProps}
          value={attributeValue != null ? String(attributeValue) : ""}
          onChange={(value) => handleValueChange(value)}
        />
      );
  }
}

/**
 * ベースプロパティを取得
 *
 * CFormItemコンポーネントの共通プロパティを生成します。
 */
function getBaseProps(attribute: Attribute): BaseProps {
  return {
    id: String(attribute.attributeKey),
    label: String(attribute.name),
    required: Boolean(attribute.required),
    readonly: Boolean(attribute.readonly),
    // colspanが指定されている場合はColSpan型にキャスト（1-7の範囲）
    colSpan: attribute.colspan as 1 | 2 | 3 | 4 | 5 | 6 | 7 | undefined,
  };
}

/**
 * ストアから現在の属性を取得
 *
 * セクションキーと属性キーに基づいて、ストアから最新の属性データを取得します。
 * 編集中の値とAPIから取得した値を適切に管理するために使用します。
 */
function getCurrentAttribute(
  attributeKey: string,
  sectionKey: string,
  storeValues: StoreValues,
): Attribute | undefined {
  const section = storeValues.sections?.find(
    (s) => String(s.sectionKey) === sectionKey,
  );
  if (section) {
    return section.attributes.find(
      (a) => String(a.attributeKey) === String(attributeKey),
    );
  }
  return undefined;
}

/**
 * ストアから特定のValueTypeのAttributeを取得する
 *
 * セクションキーとValueTypeに基づいて、ストアから最新の属性データを取得します。
 */
function findAttributeByValueType(
  valueType: ValueType,
  sectionKey: string,
  storeValues: StoreValues,
): Attribute | undefined {
  const section = storeValues.sections?.find(
    (s) => String(s.sectionKey) === sectionKey,
  );
  if (section) {
    return section.attributes.find((a) => a.valueType === valueType);
  }
  return undefined;
}
